import type {
  RaceDayCategory,
  RaceDayEvent,
  RaceDayEventDetail,
  RaceDayResults,
} from "./types";

async function getJSON<T>(url: string): Promise<T> {
  const resp = await fetch(url);
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const msg =
      (body as { error?: string })?.error ?? `Request failed (${resp.status})`;
    throw new Error(msg);
  }
  return body as T;
}

export function getEvents(): Promise<{ events: RaceDayEvent[] }> {
  return getJSON("/api/events");
}

const EVENT_DETAIL_TTL_MS = 30_000;
const eventDetailCache = new Map<
  string,
  { ts: number; value: { event: RaceDayEventDetail } }
>();

export async function getEventDetail(
  eventId: string,
): Promise<{ event: RaceDayEventDetail }> {
  const hit = eventDetailCache.get(eventId);
  if (hit && Date.now() - hit.ts < EVENT_DETAIL_TTL_MS) return hit.value;
  const value = await getJSON<{ event: RaceDayEventDetail }>(
    `/api/event/${encodeURIComponent(eventId)}`,
  );
  eventDetailCache.set(eventId, { ts: Date.now(), value });
  return value;
}

const STAGE_CATEGORIES_TTL_MS = 60_000;
const stageCategoriesCache = new Map<
  string,
  { ts: number; value: { categories: RaceDayCategory[] } }
>();

export async function getCategoriesForStage(args: {
  eventId: string;
  stageId: string;
}): Promise<{ categories: RaceDayCategory[] }> {
  const key = `${args.eventId}:${args.stageId}`;
  const hit = stageCategoriesCache.get(key);
  if (hit && Date.now() - hit.ts < STAGE_CATEGORIES_TTL_MS) return hit.value;
  const value = await getJSON<{ categories: RaceDayCategory[] }>(
    `/api/event/${encodeURIComponent(args.eventId)}/stage/${encodeURIComponent(args.stageId)}/categories`,
  );
  stageCategoriesCache.set(key, { ts: Date.now(), value });
  return value;
}

export function getResults(args: {
  stageId: string;
  categoryId: string;
  cacheBust?: boolean;
}): Promise<{ results: RaceDayResults }> {
  const url = new URL("/api/results", window.location.origin);
  url.searchParams.set("stage", args.stageId);
  url.searchParams.set("category", args.categoryId);
  if (args.cacheBust) url.searchParams.set("_t", String(Date.now()));
  return getJSON(url.pathname + url.search);
}
